import React from "react";
import { addDays, parseISO } from "date-fns";
import {
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "ui/components/form";
import { Textarea } from "ui/components/textarea";
import { FormLabel } from "components/form";
import { DatePicker } from "components/date-picker-2";
import HeaderPortal from "components/portal/header-portal";
import { PermissionControlledInput } from "permissions/components/input";
import { PermissionGate } from "permissions/components/gate";
import { formatDateForDisplay, formatDateForAPI } from "utils/utils";
import { Form } from "react-crud-form";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import ButtonWithLoading from "@/components/button-with-loading";
import { ReadField } from "@/components/field";
import { useCreateExtension } from "@/modules/work/jobs/extensions/create/mutation";
import { useUpdateExtension } from "@/modules/work/jobs/extensions/update/mutation";
import { zodResolver } from "@hookform/resolvers/zod";
import { extensionSchema } from "./schema";
import { useJob } from "../../context";
import { useFetch } from "@/hooks/useFetch";
import { fetchExtensionCategories } from "../read";
import { CategoryRadioGroup, SubCategorySelect } from "./components/category-inputs";


const ExtensionForm = ({ jobId, extensionId, defaultValues, readOnly = false }) => {
  const { job } = useJob();
  const isNewItem = !extensionId;

  const navigate = useNavigate();

  const createExtensionMutation = useCreateExtension();
  const updateExtensionMutation = useUpdateExtension();

  const { data: categories, isLoading: isCategoriesLoading } = useFetch({
    queryKey: ["job_extension_categories"],
    queryFn: fetchExtensionCategories,
  });

  const form = useForm({
    resolver: zodResolver(extensionSchema),
    defaultValues: {
      end_date: defaultValues?.end_date || "",
      justification: defaultValues?.justification || "",
      category: defaultValues?.category?.id || defaultValues?.category || "",
      reason: defaultValues?.reason?.id || defaultValues?.reason || "",
    },
  });

  const selectedCategory = form.watch("category");

  const filteredCategories = React.useMemo(() => {
    return categories
      ?.filter((c) => c.status === "published")
      .map((c) => ({
        ...c,
        children: c.children?.filter((child) => child.status === "published") || [],
      })) || [];
  }, [categories]);

  // Find the selected category object to retrieve its subcategories
  const activeCategoryObj = filteredCategories?.find(
    (c) => c.id === selectedCategory || c.value === selectedCategory
  );
  const subCategories = activeCategoryObj?.children || [];

  async function handleCreate(context) {
    const values = { ...context.values, job: jobId, status: "pending" };
    await createExtensionMutation.mutateAsync({ values });
    navigate(-1);
  }

  async function handleUpdate(id, context) {
    await updateExtensionMutation.mutateAsync({ id: extensionId || id, values: context.values });
    navigate(-1);
  }

  const permissionRule = isNewItem ? "create:*" : "update:*";

  if (readOnly) {
    const vals = defaultValues || {};

    return (
      <div className="space-y-4 max-w-md">
        <ReadField label="End Date">
          {formatDateForDisplay(vals.end_date)}
        </ReadField>
        <ReadField label="Category">{vals.category?.label}</ReadField>
        <ReadField label="Reason">{vals.reason?.label}</ReadField>
        <ReadField label="Justification">{vals.justification || "—"}</ReadField>
      </div>
    );
  }

  return (
    <Form
      form={form}
      create={handleCreate}
      update={handleUpdate}
      itemId={extensionId || "+"}
      schema={extensionSchema}
    >
      {({ isNewItem, isCreating, isUpdating, createItem, updateItem, isDirty }) => {
        const isSaving = isCreating || isUpdating;
        const submit = isNewItem ? createItem : updateItem;

        return (
          <div className="space-y-6 max-w-md">

            <FormField
              control={form.control}
              name="category"
              render={({ field }) => (
                <FormItem>
                  <PermissionControlledInput
                    type="form"
                    collection="job_extensions"
                    rule={permissionRule}
                  >
                    <FormLabel required>Category</FormLabel>
                    <FormControl>
                      {isCategoriesLoading ? (
                        <div className="text-sm text-gray-500 italic">Loading categories...</div>
                      ) : (
                        <CategoryRadioGroup
                          value={field.value}
                          onValueChange={(val) => {
                            field.onChange(val);
                            form.setValue("reason", "");
                          }}
                          categories={filteredCategories}
                          emptyMessage="No data available"
                        />
                      )}
                    </FormControl>
                  </PermissionControlledInput>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="reason"
              render={({ field }) => (
                <FormItem>
                  <PermissionControlledInput
                    type="form"
                    collection="job_extensions"
                    rule={permissionRule}
                  >
                    <FormLabel required>Reason</FormLabel>
                    <FormControl>
                      <CategoryRadioGroup
                        value={field.value}
                        onValueChange={field.onChange}
                        categories={subCategories}
                        disabled={!selectedCategory || isCategoriesLoading}
                        disabledMessage="Select a category first to view reasons"
                        emptyMessage="No reasons available for this category"
                      />
                    </FormControl>
                  </PermissionControlledInput>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="justification"
              render={({ field }) => (
                <FormItem>
                  <PermissionControlledInput
                    type="form"
                    collection="job_extensions"
                    rule={permissionRule}
                  >
                    <FormLabel required>Justification</FormLabel>
                    <FormControl>
                      <Textarea {...field} className="resize-none min-h-24 w-full" />
                    </FormControl>
                  </PermissionControlledInput>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="end_date"
              render={({ field }) => (
                <FormItem>
                  <PermissionControlledInput
                    type="form"
                    collection="job_extensions"
                    rule={permissionRule}
                  >
                    <FormLabel required>End Date</FormLabel>
                    <FormControl>
                      <DatePicker
                        {...field}
                        min={formatDateForAPI(addDays(parseISO(job.end_date), 1))}
                      />
                    </FormControl>
                  </PermissionControlledInput>
                  <FormMessage />
                </FormItem>
              )}
            />


            <HeaderPortal>
              <PermissionGate
                collection="job_extensions"
                rule={permissionRule}
                fallback={
                  <div className="text-sm text-gray-500 italic">
                    You don't have permission to {isNewItem ? "create" : "update"} this extension.
                  </div>
                }
              >
                <ButtonWithLoading
                  variant="default"
                  isLoading={isSaving}
                  onClick={submit}
                  disabled={!isDirty}
                >
                  {isNewItem ? "Save" : "Update"}
                </ButtonWithLoading>
              </PermissionGate>
            </HeaderPortal>
          </div>
        );
      }}
    </Form>
  );
};

export default ExtensionForm;

